import { Cluster } from 'frontend/clusters'
import TableFileThumbnails from 'frontend/common/files/components/table-file-thumbnails.vue'
import { useActiveStorageBlobUrl } from 'frontend/common/files/composables/useActiveStorageBlobUrl'
import TrueFalseLabel from 'frontend/common/true-false-label.vue'
import { Depot } from 'frontend/depots'
import { LocationType } from 'frontend/location-types'
import { StagingArea } from 'frontend/staging-areas'
import { User } from 'frontend/users'
import { markRaw } from 'vue'

import TransportationPoints from './transportation-points.vue'

const { mapResourceImageToFileResponseEntity } = useActiveStorageBlobUrl()
export const FIELDS = [
  'id',
  'eid',
  'name',
  'time_zone',
  'abbreviation',
  'address',
  'description',
  'latitude',
  'longitude',
  'remote_id',
  'iata_code',
  'updated_at',
  'created_at',
  'locked_cluster_assignment',
  { location_images: ['id', 'is_primary', 'urls'] },
  { cluster: ['id', 'name', 'time_zone'] },
  { location_type: ['id', 'name'] },
  { transportation_points: ['id', 'name'] },
  { created_by: ['id', 'email', 'name', 'surname'] },
  { depot: ['id', 'name'] },
  // Arel relations
  {
    staging_areas: ['id', 'name'],
  },
]

export const SUBSCRIPTIONS = {
  items: 'LocationsChannel',
  clusters: 'ClustersSimpleChannel',
  locationTypes: 'LocationTypesSimpleChannel',
  depots: 'DepotsFlatChannel',
  transportationPoints: 'TransportationPointsSimpleChannel',
}

export const DEFAULT_COLUMNS = [
  'eid',
  'lowerName',
  'timeZone',
  'abbreviation',
  'address',
  'coordinates',
  'cluster',
  'lockedClusterAssignment',
  'locationType',
  'stagingAreas',
  'depot',
  'transportationPoints',
  'remoteId',
  'updatedDatetime',
]

export const COLUMNS_CONFIG = {
  eid: {
    label: 'EID',
    header: {
      sort: 'eid',
    },
    cell: {
      kind: 'simple',
      path: 'eid',
    },
  },
  lowerName: {
    label: 'Name',
    header: {
      sort: 'lowerName',
    },
    cell: {
      kind: 'simple',
      path: 'name',
    },
  },
  timeZone: {
    label: 'Time zone',
    header: {
      sort: 'timeZone',
    },
    cell: {
      kind: 'simple',
      path: 'timeZone',
    },
  },
  abbreviation: {
    label: 'Abbreviation',
    header: {
      sort: 'abbreviation',
    },
    cell: {
      kind: 'simple',
      path: 'abbreviation',
    },
  },
  address: {
    label: 'Address',
    header: {
      sort: 'address',
    },
    cell: {
      kind: 'simple',
      path: 'address',
    },
  },
  coordinates: {
    label: 'Coordinates',
    header: {
      sort: 'latitude',
    },
    cell: {
      kind: 'component',
      component: 'TrueFalseLabel',
      componentProps: location => ({
        item: !!(location.latitude && location.longitude),
      }),
    },
  },
  latitude: {
    label: 'Latitude',
    header: {
      sort: 'latitude',
    },
    cell: {
      kind: 'function',
      fn(item) {
        return item.latitude ? Number(item.latitude).toFixed(6) : ''
      },
    },
  },
  longitude: {
    label: 'Longitude',
    header: {
      sort: 'longitude',
    },
    cell: {
      kind: 'function',
      fn(item) {
        return item.longitude ? Number(item.longitude).toFixed(6) : ''
      },
    },
  },
  iataCode: {
    label: 'IATA code',
    header: {
      sort: 'iataCode',
    },
    cell: {
      kind: 'simple',
      path: 'iataCode',
    },
  },
  cluster: {
    label: 'Cluster',
    header: {
      sort: 'clusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: location => ({ cluster: location.cluster }),
    },
  },
  description: {
    label: 'Description',
    header: {
      sort: 'description',
    },
    cell: {
      kind: 'component',
      component: 'ellipsis-with-tooltip',
      componentProps: item => {
        return {
          text: item.description,
          limit: 250,
        }
      },
    },
  },
  lockedClusterAssignment: {
    label: 'Locked cluster assignment',
    header: {
      sort: 'lockedClusterAssignment',
    },
    cell: {
      kind: 'component',
      component: TrueFalseLabel,
      componentProps: item => ({ item: item.lockedClusterAssignment }),
    },
  },
  locationType: {
    label: 'Type',
    header: {
      sort: 'locationTypeName',
    },
    cell: {
      kind: 'component',
      component: markRaw(LocationType),
      componentProps: location => ({ locationType: location.locationType }),
    },
  },
  stagingAreas: {
    label: 'Staging areas',
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.stagingAreas,
        limit: 5,
        component: markRaw(StagingArea),
        componentProps: stagingArea => ({
          stagingArea,
          hideYes: true,
        }),
      }),
    },
  },
  depot: {
    label: 'Depot',
    header: {
      sort: 'depotEid',
    },
    cell: {
      kind: 'component',
      component: markRaw(Depot),
      componentProps: location => ({ depot: location.depot }),
    },
  },
  transportationPoints: {
    label: 'Transportation points',
    cell: {
      kind: 'component',
      component: markRaw(TransportationPoints),
      componentProps: location => ({ location: location }),
    },
  },
  remoteId: {
    label: 'Remote ID',
    header: {
      sort: 'remoteId',
    },
    cell: {
      kind: 'simple',
      path: 'remoteId',
    },
  },
  createdBy: {
    label: 'Created by',
    header: {
      sort: 'createdByUsername',
    },
    cell: {
      kind: 'component',
      component: markRaw(User),
      componentProps: item => ({
        user: item.createdBy,
      }),
    },
  },
  updatedDatetime: {
    label: 'Updated Datetime',
    header: {
      sort: 'updatedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: location => ({
        t: location.updatedAt,
      }),
    },
  },
  updatedDate: {
    label: 'Updated Date',
    header: {
      sort: 'updatedAtDate',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: location => ({
        t: location.updatedAt,
        format: 'dateOnly',
      }),
    },
  },
  updatedTime: {
    label: 'Updated Time',
    header: {
      sort: 'updatedAtTime',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: location => ({
        t: location.updatedAt,
        format: 'timeOnly',
      }),
    },
  },
  createdDatetime: {
    label: 'Created Datetime',
    header: {
      sort: 'createdAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: location => ({
        t: location.createdAt,
      }),
    },
  },
  createdDate: {
    label: 'Created Date',
    header: {
      sort: 'createdAtDate',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: location => ({
        t: location.createdAt,
        format: 'dateOnly',
      }),
    },
  },
  createdTime: {
    label: 'Created Time',
    header: {
      sort: 'createdAtTime',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: location => ({
        t: location.createdAt,
        format: 'timeOnly',
      }),
    },
  },
  images: {
    label: 'Images',
    cell: {
      kind: 'component',
      component: TableFileThumbnails,
      componentProps: location => ({
        files: location.locationImages?.map(locationImage => {
          return mapResourceImageToFileResponseEntity(locationImage)
        }),
        eid: location.eid,
      }),
    },
  },
}
